import React, { useEffect } from 'react';
import { Nullable } from 'global';
import makeStyles from '@mui/styles/makeStyles';
import { FormProvider, useForm } from 'react-hook-form';
import { Box, LinearProgress, Typography } from '@mui/material';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { CreditLineRequest } from '@trnsact/trnsact-shared-types';

import { useVO } from '../../hooks';
import { CreditLineForm } from './CreditLineForm';
import { getCreditLineFormDefaultValues } from '../lib';

interface Props {
  userName: string;
  isCreditLineRequested: boolean;
  creditLineRequest: Nullable<CreditLineRequest>;
}

export const CreditLineCard = ({ userName, isCreditLineRequested, creditLineRequest }: Props) => {
  const classes = useStyles();

  const form = useForm({ defaultValues: getCreditLineFormDefaultValues(creditLineRequest) });

  // Re-sync the form with the latest server data (e.g. after a scorecard run refreshes the VO)
  // unless the user has unsaved edits, which should not be discarded.
  useEffect(() => {
    if (form.formState.isDirty) return;

    form.reset(getCreditLineFormDefaultValues(creditLineRequest));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    creditLineRequest?.status,
    creditLineRequest?.notes,
    creditLineRequest?.approvedAmount,
    creditLineRequest?.requestedAmount,
    creditLineRequest?.expirationDate,
    creditLineRequest?.lenderProfileId,
  ]);

  const { updateCreditLineRequest, isUpdateCreditLineLoading } = useVO();

  return (
    <CardContainer title="Credit Line" containerClassName={classes.container} sx={{ width: { xs: '100%', md: '50%' } }}>
      <RenderWithFallback
        isLoading={isUpdateCreditLineLoading}
        fallBack={
          <Box>
            <Typography>Updating Credit Line...</Typography>
            <LinearProgress style={{ width: '100%' }} />
          </Box>
        }
      >
        <RenderWithCondition condition={isCreditLineRequested} fallBack={<Typography>Not Requested</Typography>}>
          <form className={classes.cardContainer}>
            <FormProvider {...form}>
              <CreditLineForm
                userName={userName}
                status={form.getValues('status')}
                isSaving={isUpdateCreditLineLoading}
                onCreditLineUpdate={updateCreditLineRequest}
                requestedAmount={form.getValues('requestedAmount')}
                creditLineRequest={creditLineRequest ? creditLineRequest : undefined}
              />
            </FormProvider>
          </form>
        </RenderWithCondition>
      </RenderWithFallback>
    </CardContainer>
  );
};

const useStyles = makeStyles({
  // Card itself must not clip/scroll; only the Notes textarea (its own maxRows) should scroll.
  container: {
    height: 'auto !important',
    overflow: 'visible !important',
  },
  cardContainer: {
    gap: '1rem',
    display: 'flex',
    flexDirection: 'column',

    '& .lineSection': {
      gap: '1rem',
      display: 'flex',
      flexDirection: 'column',

      '& .form': {
        gap: '1rem',
        display: 'flex',
        flexDirection: 'column',
      },
    },

    '& .notesSection': {
      gap: '1rem',
      display: 'flex',
      flexDirection: 'column',

      '& .customNote': {
        gap: '1rem',
        display: 'flex',
        alignItems: 'flex-end',
      },
    },
  },
});
